import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

export type ReviewCheckId =
    | 'competing-focus'
    | 'phases-not-staged'
    | 'accumulate-only'
    | 'slideshow-cut'
    | 'dead-element'
    | 'unnameable-object'
    | 'text-hierarchy-mismatch'
    | 'text-split-across-sizes'
    | 'text-clipped'
    | 'content-off-frame'
    | 'content-obscured'
    | 'media-mismatch';

export interface ReviewCheck {
    id: ReviewCheckId;
    /** What the reviewer should look for, phrased as an observation about the frames. */
    fires: string;
    /** Needs more than one frame to judge, so it is skipped when a single instant was captured. */
    needsScan: boolean;
    /**
     * Only meaningful for a scene holding an HtmlAsset — a graphic the agent wrote itself. A
     * template scene animates in and settles under a designer's timeline and has no phases to
     * stage, so these checks would report the norm there rather than a defect.
     */
    htmlGraphicOnly: boolean;
}

export const REVIEW_CHECKS: readonly ReviewCheck[] = [
    {
        id: 'competing-focus',
        fires: 'several elements compete for first read; no single element leads',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'phases-not-staged',
        fires:
            'everything is already present in the first sampled frame and nothing changes for ' +
            'the rest of the scene, or phases land together instead of each taking its own ' +
            'stretch of the timeline',
        needsScan: true,
        htmlGraphicOnly: true,
    },
    {
        id: 'accumulate-only',
        fires:
            'the frame ends crowded because it only ever accumulates — across the phases nothing ' +
            'shrinks, moves aside, or leaves to make room for what comes next',
        needsScan: true,
        htmlGraphicOnly: true,
    },
    {
        id: 'slideshow-cut',
        fires: 'the frame swaps wholesale from one state to the next, with nothing carrying across',
        needsScan: true,
        htmlGraphicOnly: true,
    },
    {
        id: 'dead-element',
        fires:
            'something on screen earns no place — a second idea crowding the first, a label ' +
            'restating the narration, a line saying what the line above it already said, or an ' +
            'element filling a gap',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'unnameable-object',
        fires:
            "a drawn object cannot be named from its shape alone, without reading the scene's " +
            'text or narration for a hint — a shape you can only describe ("a ring with marks in ' +
            'it", "a squiggle in a circle") reads as nothing',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'text-hierarchy-mismatch',
        fires:
            'the scene makes TWO points but sets one much bigger than the other. Transcribe both ' +
            'lines and ask whether the smaller one is genuinely SUBORDINATE to the larger — does ' +
            'it elaborate, qualify, attribute or label it? If it does, that is correct hierarchy ' +
            'and you must NOT report it. If instead the two stand as separate points of their ' +
            'own — two claims, two credentials, two parallel clauses, two steps of a process — ' +
            'they are coordinate, the size gap ranks them for no reason, and this fires',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'text-split-across-sizes',
        fires:
            'ONE sentence is spread over two text assets of very different size. Transcribe ' +
            'both lines and read them as a single string: if that string is one grammatical ' +
            'sentence, this fires — the halves of one thought have been assigned different ' +
            'importance. Report it even though the sentence reads correctly in order; the ' +
            'defect is the demotion, not legibility. One exception: when the smaller line sits ' +
            'ABOVE the larger one and both are short, it is a deliberate lead-in into a payoff ' +
            'and must NOT be reported',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'text-clipped',
        fires:
            'text overflowing its box, cut off, too small to read, or spilling outside a shape ' +
            'that bounds it (a circle, badge or pill)',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'content-off-frame',
        fires: 'any element cut off by a frame edge — not only text',
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'content-obscured',
        fires: "an element covers a face, the media's subject, or another element, in a way that reads as unintended",
        needsScan: false,
        htmlGraphicOnly: false,
    },
    {
        id: 'media-mismatch',
        fires:
            'a photo or video that is clearly unrelated to the scene, or reads as generic stock — ' +
            'a staged office, a thumbs-up, a lightbulb/gears/rocket metaphor, a dated look, or ' +
            'baked-in VFX like particles, light streaks or hologram graphics',
        needsScan: false,
        htmlGraphicOnly: false,
    },
];

export interface ReviewFinding {
    check: ReviewCheckId;
    scene_id: string;
    detail: string;
}

export function applicableChecks({
    hasScan,
    isHtmlGraphic,
}: {
    hasScan: boolean;
    isHtmlGraphic: boolean;
}): readonly ReviewCheck[] {
    return REVIEW_CHECKS.filter(
        (check) => (hasScan || !check.needsScan) && (isHtmlGraphic || !check.htmlGraphicOnly),
    );
}

/**
 * The reviewer's brief. The threshold sentence is load-bearing: with no severity tier, it is the
 * only thing separating a defect from an observation, and an unfiltered reviewer would block on
 * commentary like "the caption adds visual weight on the right".
 */
export function buildReviewInstruction(checks: readonly ReviewCheck[]): string {
    const list = checks.map((check) => `- ${check.id}: ${check.fires}`).join('\n');
    // toolNameReferences.spec.ts forbids a literal tool-name string anywhere under src/agent —
    // the name must come from TOOL_NAMES so a rename propagates.
    return `Review the frames above against this closed list of defects.

${list}

Two things on screen are intentional. Stock media carries a watermark in these editor renders and
is licensed and unwatermarked at render time. A small logo held in a corner across scenes is the
video's own brand watermark, placed on purpose. Never report either.

Report only what the author should change. If you would caveat a finding — "slightly", "minor",
"but it still reads" — it is not a defect and you must not report it. Report nothing that is not
on the list, however much it stands out. A scene with no defects gets an empty list.

Report every finding through the ${TOOL_NAMES.reportFindings} tool. Use the scene_id exactly as it is labelled above,
and make each detail concrete enough to act on without seeing the frames.`;
}

/**
 * Order-independent identity for a finding set, including its free-form `detail` text. Used only
 * to call out an edit that provably changed nothing — the wording must match byte-for-byte for
 * that claim to be honest, since the reviewer is re-describing freshly rendered frames each round
 * and any wording drift means it actually looked again.
 */
export function findingsSignature(findings: readonly ReviewFinding[]): string {
    return findings
        .map((finding) => `${finding.check}|${finding.scene_id}|${finding.detail}`)
        .sort()
        .join('\n');
}

/**
 * Order-independent identity for a finding set by `check` + `scene_id` alone, deliberately
 * ignoring `detail`. Drives the round cap: the model is not required to phrase the same defect
 * identically call to call, so gating the cap on exact wording lets any reworded sentence reset an
 * unfixable defect's round count forever. This is "the same defect, still there", not "the same
 * sentence, still there" — that stricter question belongs to `findingsSignature`.
 */
export function findingsIdentitySignature(findings: readonly ReviewFinding[]): string {
    return findings
        .map((finding) => `${finding.check}|${finding.scene_id}`)
        .sort()
        .join('\n');
}

/**
 * Checks whose fix is a real procedure rather than something the reviewer's one-sentence `detail`
 * can carry. The `detail` is free text the vision model writes fresh each round, so without this
 * the agent improvises a remedy from a description of the defect — which is how the contrast doom
 * loop (AP-926) happened. Matches how the widow lint and set_text point at fix-text-wrapping.
 */
const FIX_SKILL_BY_CHECK: Partial<Record<ReviewCheckId, string>> = {
    'text-hierarchy-mismatch': SKILL_NAMES.fixTextHierarchy,
    'text-split-across-sizes': SKILL_NAMES.fixTextHierarchy,
    'text-clipped': SKILL_NAMES.fixTextWrapping,
};

export function formatReviewGate(
    findings: readonly ReviewFinding[],
    options: { repeated: boolean },
): string {
    if (findings.length === 0) {
        return 'Review found no defects.';
    }
    const lead = options.repeated
        ? `Review found the same ${findings.length} defect(s), unchanged from your previous attempt — that edit did not affect them. Look at the scene and change your approach instead of resending an equivalent fix:`
        : `Review found ${findings.length} defect(s) that must be fixed:`;
    const list = findings
        .map(
            (finding) =>
                `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}${FIX_SKILL_BY_CHECK[finding.check] ? ` Load the ${FIX_SKILL_BY_CHECK[finding.check]} skill for the fix procedure.` : ''}`,
        )
        .join('\n');
    return `${lead}\n${list}`;
}
